import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { interval, Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import * as NotificationsActions from "../../../../store/notifications/notifications.actions";
import { AccessPermission, AccessPermissionPoll, PermittedAccessThs, PermittedCommand } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { LOCALSTORAGE_PIN_KEY_PREFIX } from 'src/app/conf/permissions';
import { ActivatedRoute } from '@angular/router';
import { selectAccessPermissionThs, selectCommandExecutionError, selectCommandExecutionErrorId, selectPermissionPoll, selectShowPinPad, selectValidPin, selectValidToken } from 'src/app/store/permissions/permissions.selector';
import { AppComponent } from 'src/app/app.component';
import { Lang } from 'src/app/model/lang';
import { DEFAULT_LANG, LANGUAGES } from 'src/app/conf/languages';
import { selectCurrentLang } from 'src/app/store/user/user.selector';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { Thermo } from 'src/app/model/thermo';
import { TH_MANUAL_TEMP_STEP } from 'src/app/conf/thermo';
import { TranslateService } from '@ngx-translate/core';

@Component({
	selector: 'app-send-command',
	templateUrl: './send-command.component.html',
})

export class SendCommandComponent implements OnInit, OnDestroy {
	// Store selectors
	validToken$: Observable<boolean | undefined>;
	validTokenSub: Subscription | undefined;
	validPin$: Observable<boolean | undefined>;
	validPinSub: Subscription | undefined;
	showPinPad$: Observable<boolean | undefined>;
	showPinPadSub: Subscription | undefined;
	commandExecutionError$: Observable<string | undefined>;
	commandExecutionErrorSub: Subscription | undefined;
	commandExecutionErrorId$: Observable<number | undefined>;
	commandExecutionErrorIdSub: Subscription | undefined;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;
	languages = LANGUAGES;

	token = '';
	validToken = true;

	pin = '';
	validPin: boolean;

	accessPermission: AccessPermission;
	accessPermission$: Observable<AccessPermission | undefined>;
	accessPermissionSub: Subscription | undefined;

	commandExecutionError: string;
	commandExecutionErrorId: number;

	commandListSize: number;
	columnNumber: string;

	showPinPad: boolean;

	automationList: Map<string, PermittedCommand[]>;
	permittedAccessThs: PermittedAccessThs[];

	timerUpdateThermo: number = 0;

	resetPin = false;

	bnbThermalControlOptions: any[] = [];

	readonly THS_INFO_POLL_MS = 30 * 1000; // 30 seconds -> don't decrease for load balance in production
	pollingSub?: Subscription;
	permissionPoll$: Observable<AccessPermissionPoll | undefined>;
	permissionPollSub: Subscription | undefined;

	// Design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private route: ActivatedRoute,
		public app: AppComponent,
		private translate: TranslateService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.validToken$ = this.store.select(selectValidToken);
		this.validPin$ = this.store.select(selectValidPin);
		this.showPinPad$ = this.store.select(selectShowPinPad);
		this.commandExecutionError$ = this.store.select(selectCommandExecutionError);
		this.commandExecutionErrorId$ = this.store.select(selectCommandExecutionErrorId);
		this.accessPermission$ = this.store.select(selectAccessPermissionThs);
		this.permissionPoll$ = this.store.select(selectPermissionPoll);
	}

	ngOnInit() {
		this.store.dispatch(NotificationsActions.setTopbarTitleId({
			stringId: 'QUICK_EASY'
		}));
		this.token = this.route.snapshot.params['token'];
		this.pin = localStorage.getItem(`${LOCALSTORAGE_PIN_KEY_PREFIX}${this.token}`);
		if (this.pin) {
			this.sendCommand(new PermittedCommand());
		}
		this.store.dispatch(PermissionActions.getTokenPermissionCommands({ token: this.token }));
		this.validTokenSub = this.validToken$.subscribe(valid => {
			this.validToken = valid;
		});
		this.validPinSub = this.validPin$.subscribe(valid => {
			this.validPin = valid;
		});
		this.showPinPadSub = this.showPinPad$.subscribe(show => {
			this.showPinPad = show;
		});
		this.accessPermissionSub = this.accessPermission$.subscribe(accessPermission => {
			this.accessPermission = accessPermission;
			this.automationList = new Map<string, PermittedCommand[]>();
			if (this.accessPermission !== undefined) {
				for (const command of this.accessPermission.Commands) {
					if (!this.automationList.has(command.DeviceName.toUpperCase())) {
						const list = [];
						list.push(command);
						this.automationList.set(command.DeviceName.toUpperCase(), list);
						this.commandListSize = list.length;
					} else {
						this.automationList.get(command.DeviceName.toUpperCase()).push(command);
					}
				}
				this.automationList.forEach((value: PermittedCommand[]) => {
					value.sort((a, b) => {
						if (a.CommandDescription.toLowerCase() < b.CommandDescription.toLowerCase()) // sort string ascending
							return -1;
						return 1;
					});
				});
				if (accessPermission.Ths) {
					this.initBnbThermalControlOptions();
					this.permittedAccessThs = accessPermission.Ths;
					this.permittedAccessThs.forEach((permAccTh: PermittedAccessThs) => {
						this.applyCurrentSeasonToMode(permAccTh);
						this.setMinMaxTemp(permAccTh);
					});
					this.startPolling();
				}
			}
		});
		this.permissionPollSub = this.permissionPoll$.subscribe(permissionPoll => {
			if (permissionPoll && this.accessPermission) {
				if (this.accessPermission.Start !== permissionPoll.Start) {
					this.accessPermission.Start = permissionPoll.Start;
				}
				if (this.accessPermission.End !== permissionPoll.End) {
					this.accessPermission.End = permissionPoll.End;
				}
				if (permissionPoll.Ths && this.hasThsChanged(this.permittedAccessThs, permissionPoll.Ths)) {
					console.log('UPDATED THS INFOS FROM POLLING');
					this.permittedAccessThs = permissionPoll.Ths;
					this.permittedAccessThs.forEach((permAccTh: PermittedAccessThs) => {
						this.applyCurrentSeasonToMode(permAccTh);
						this.setMinMaxTemp(permAccTh);
					});
				}
			}
		});

		this.commandExecutionErrorSub = this.commandExecutionError$.subscribe(commandExecutionError => {
			this.commandExecutionError = commandExecutionError;
		});

		this.commandExecutionErrorIdSub = this.commandExecutionErrorId$.subscribe(commandExecutionErrorId => {
			if (commandExecutionErrorId === null) {
				this.commandExecutionErrorId = 0;
			} else {
				this.commandExecutionErrorId = commandExecutionErrorId;
			}
		});
		this.columnNumber = this.getColumnNumber();

		this.currentLang$ = this.store.select(selectCurrentLang);
		this.currentLang$?.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});

		this.onResize();
	}

	initBnbThermalControlOptions(): void {
		this.bnbThermalControlOptions = [
			{ icon: 'pi cm-power-off', name: this.translate.instant('TH_OFF'), value: 'off' },
			{ icon: 'pi cm-handle-set', name: this.translate.instant('TH_MANUAL'), value: 'manual' },
		];
	}

	ngOnDestroy(): void {
		this.validTokenSub?.unsubscribe();
		this.validPinSub?.unsubscribe();
		this.showPinPadSub?.unsubscribe();
		this.accessPermissionSub?.unsubscribe();
		this.permissionPollSub?.unsubscribe();
		this.stopPolling();
		if (this.timerUpdateThermo !== 0) {
			window.clearTimeout(this.timerUpdateThermo);
		}
	}

	onPinInserted(event: number[]) {
		this.pin = event.join('');
		this.sendCommand(new PermittedCommand());
	}

	sendCommand(cmd: PermittedCommand) {
		this.store.dispatch(PermissionActions.executeTokenCommand({
			token: this.token,
			pin: this.pin,
			command: cmd,
		}));
	}

	// -------------- TH management
	startPolling(): void {
		this.stopPolling(); // stop refused polling

		console.log('START ACCESS POLLING')
		this.pollingSub = interval(this.THS_INFO_POLL_MS).subscribe(() => {
			this.store.dispatch(PermissionActions.getPollPermissionAccessInfoTh({ token: this.token }));
		});
	}

	stopPolling(): void {
		if (this.pollingSub) {
			console.log('END ACCESS POLLING')
			this.pollingSub.unsubscribe();
			this.pollingSub = undefined; // Reset del riferimento
		}
	}

	// ------ polling aux functs
	private hasThsChanged(currentThs: PermittedAccessThs[] | undefined, pollingThs: PermittedAccessThs[]): boolean {
		if (!currentThs || currentThs.length !== pollingThs.length) { // sono stati aggiunti o rimossi dei ths al permesso
			return true;
		}
		return JSON.stringify(this.normalizeThs(currentThs)) !== JSON.stringify(this.normalizeThs(pollingThs));
	}

	// mapping object Th for analyze and compare changes
	private normalizeThs(ths: PermittedAccessThs[]): object[] {
		return ths.map(th => ({
			DeviceId: th.DeviceId,
			DeviceName: th.DeviceName,
			Keycode: th.Keycode,
			ManualTemp: th.ManualTemp,
			TempLoc: th.TempLoc,
			HumLoc: th.HumLoc,
			Mode: th.CurrentSeason === 'off' ? 'off' : th.Mode, // If CurrentSeason is "off", force Mode to "off".
			CurrentSeason: th.CurrentSeason,
			RelayStatus: th.RelayStatus,
			Online: th.Online,
			Bb: th.Bb ? {
				DeltaTempN: th.Bb.DeltaTempN,
				DeltaTempP: th.Bb.DeltaTempP,
				Enable: th.Bb.Enable,
				Temp: th.Bb.Temp,
			} : undefined,
			Qe: th.Qe ? {
				CourtesyTime: th.Qe.CourtesyTime,
				Enable: th.Qe.Enable,
				StdbyTemp: th.Qe.StdbyTemp,
			} : undefined,
		}));
	}
	// ------ polling aux functs

	/**
	 * If CurrentSeason is "off", force Mode to "off".
	 * Otherwise keep Mode as received from first GET info or from GET ths poll.
	 */
	private applyCurrentSeasonToMode(th: PermittedAccessThs): void {
		if (th.CurrentSeason === 'off') {
			th.Mode = 'off';
		}
	}

	getStatusThermoOnlineBnBQnE(th: PermittedAccessThs): boolean {
		if (th.Online !== 1) {
			return false;
		} else if (th.Bb.Enable !== true || th.Qe.Enable !== true) {
			return false;
		} else {
			return true; // online and B&B and Q&E are enabled
		}
	}

	getStatusThermoString(th: PermittedAccessThs): string {
		if (th.Online !== 1) {
			return this.translate.instant('OFFLINE_NOT_REACHABLE_THERMO');
		} else if (th.Bb.Enable !== true || th.Qe.Enable !== true) {
			return this.translate.instant('TH_Q&E_DISABLE_ALERT');
		} else {
			return ''; // online and B&B and Q&E are enabled
		}
	}

	// aux func needed for right operation
	// round the typescript float number (0.50004 etc -> 0.5)
	getValueFloat(value: number): number {
		return parseFloat(value.toFixed(1));
	}

	setMinMaxTemp(th: PermittedAccessThs): void {
		const bbTemp: number = th.Bb?.Temp;
		const deltaTempN: number = th.Bb?.DeltaTempN;
		const deltaTempP: number = th.Bb?.DeltaTempP;

		if (bbTemp !== undefined && bbTemp !== null &&
			deltaTempN !== undefined && deltaTempN !== null &&
			deltaTempP !== undefined && deltaTempP !== null
		) {
			const minTemp = bbTemp - deltaTempN;
			th.Bb.thManualTempMin = this.getValueFloat(minTemp);

			const maxTemp = bbTemp + deltaTempP;
			th.Bb.thManualTempMax = this.getValueFloat(maxTemp);

			th.Bb.thManualTempStepsValue = TH_MANUAL_TEMP_STEP;
		} else {
			return;
		}
	}

	bnbThermalControlChanged(th: PermittedAccessThs): void {
		this.stopPolling();

		this.timerAndUpdate(th, 'mode');
	}

	executeThermo(th: PermittedAccessThs, thermoUpdate: Thermo): void {
		// POST execute
		this.store.dispatch(PermissionActions.executeTokenCommand({
			token: this.token,
			pin: this.pin,
			thCommand: thermoUpdate
		}));
	}

	timerAndUpdate(th: PermittedAccessThs, setMode: string): void {
		// DEBOUNCE: catch active timer and reset it if it exists
		if (this.timerUpdateThermo !== 0) {
			window.clearTimeout(this.timerUpdateThermo);
		}

		// start timer and saving on the right timer variable
		this.timerUpdateThermo = window.setTimeout(() => {
			const thermoUpdate = new Thermo();
			thermoUpdate.keycode = th.Keycode;
			if (setMode === 'temp') {
				thermoUpdate.set_manual_temp = th.ManualTemp;
			} else if (setMode === 'mode') {
				thermoUpdate.set_mode = th.Mode;
			}
			this.executeThermo(th, thermoUpdate);
			this.timerUpdateThermo = 0; // Reset del timer
		}, 1500); // waiting 1.5 secs after last click release
	}

	updatingSpinnerValue(eventValue: number, th: PermittedAccessThs) {
		const valueFixed = Number(Number(eventValue).toFixed(1));
		th.ManualTemp = valueFixed;
	}

	// Slider p-knob / +/- buttons user update value
	sliderTempStop(eventValue: number, th: PermittedAccessThs): void {
		this.stopPolling();

		this.updatingSpinnerValue(eventValue, th);

		this.timerAndUpdate(th, 'temp');
	}

	// -------------- TH management

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	getColumnNumber(): string {
		if (this.commandListSize === 2) {
			return 'col-6';
		} else if (this.commandListSize >= 3) {
			return 'col-4';
		} else {
			return 'col-12';
		}
	}
}